import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import get from 'lodash/get';
import currencyFormatter from 'currency-formatter';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import LocationPin from 'yoda-core-components/lib/assets/svg/LocationPin.svg';
import ItemThumbnailsList from 'yoda-checkout-components/lib/components/ItemThumbnailsList/ItemThumbnailsList';
import WarningMessage from 'yoda-checkout-components/lib/components/WarningMessage/WarningMessage';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import {
    CURRENCY_US,
    EXPOSED_PACKAGING_CHECKOUT_MSG,
} from 'yoda-checkout-components/lib/common/Constants';
import ShippingService from '../../services/ShippingService';
import checkoutStaticText from '../../helpers/LabelConstantsCheckout';
import SilverItemDeliveryInformation from '../SilverItemDeliveryInformation/SilverItemDeliveryInformation';

const PromiseDeliveryDates = ({
    orderItems,
    enableExposedPackagingMessage,
    truckableDeliveryMessage,
    reviewPage,
    shippingMethods,
    orderCharges,
    isOcpPage,
    shippingType,
    orderShippingGroupsInfo,
    isSingleShippingMethod,
}) => {
    const getPromiseDates = ShippingService.getPromiseDeliveryDates(
        reviewPage ? orderItems?.bagItems : orderItems,
        isOcpPage
    );
    let deliveryMethod = {};
    if (!reviewPage) {
        deliveryMethod = shippingMethods ? find(shippingMethods, { code: 'TRUCK' }) : {};
    } else if (orderItems?.type === 'STORE') {
        const shipToStoreCharges = !isEmpty(orderCharges)
            ? find(orderCharges, { type: 'SHIPPING AND HANDLING' })
            : {};
        deliveryMethod = {
            charge: shipToStoreCharges?.amount,
            duration: checkoutStaticText.STANDARD_DELIVERY,
        };
    } else {
        const deliveryDataList = get(orderItems, 'serviceLevels.data', []);
        deliveryMethod = find(
            deliveryDataList,
            (shippingMethod) => shippingMethod.selected === true
        );
    }
    deliveryMethod = deliveryMethod || {};
    // There is difference in charge between review and shipping section
    if (deliveryMethod?.charge === 0) {
        deliveryMethod.charge = '$0.00';
    }
    const dateComparison = (a, b) => {
        const date1 = new Date(a);
        const date2 = new Date(b);
        return date1 - date2;
    };
    const showStandardDeliveryTextForRegularItems =
        (!isEmpty(getPromiseDates.truckable.items) && !isOcpPage) || reviewPage;
    const renderBopisProducts = () => {
        const pickupDateMessage = orderShippingGroupsInfo[0].pickupDateMessage || 'Today';
        return (
            <div data-automation-id="at-ocp-bopis-promise-delivery-date">
                <p className={dt(['font-open-sans-semibold', 'text-medium', 'pb-4', 'pt-6'])}>
                    Same Day Pickup
                </p>
                <div className={dt(['flex', 'items-baseline'])}>
                    <div className={dt(['mr-1', 'text-gray-70'])}>
                        <Icon iconType="svg" width="20px" height="20px" viewBox="0 0 48 48">
                            <LocationPin />
                        </Icon>
                    </div>
                    <div>
                        <p
                            className={dt(['font-open-sans-semibold', 'text-small', 'pb-1'])}
                            data-automation-id="at-ocp-bopis-promise-delivery-date-storename"
                        >
                            {getPromiseDates?.bopis?.storeName}
                        </p>
                        <p className={dt(['font-open-sans', 'text-small', 'pb-4', 'text-gray-70'])}>
                            We’ll notify you when ready for pickup.
                        </p>
                    </div>
                </div>
                <p className={dt(['font-open-sans-semibold', 'text-small', 'pb-2'])}>
                    Pickup {pickupDateMessage}
                </p>
                <ItemThumbnailsList
                    orderItems={orderItems}
                    items={getPromiseDates.bopis.items}
                    automationId="at-bopis-thumb-list"
                    enableExposedPackagingMessage={enableExposedPackagingMessage}
                    isPromseDateDelivery
                />
                <div className={dt(['h-[1px]', 'bg-gray-15', 'mt-2', 'px-4', 'py-0'])} />
            </div>
        );
    };
    const renderTruckableProducts = () => {
        const truckProducts = [];
        const shipAlone = find(getPromiseDates.truckable.items, {
            product: { shipAlone: true },
        });
        if (isOcpPage) {
            const truckItems = Object.keys(getPromiseDates.truckable.ocpGroupItems);
            truckItems.forEach((item, index) => {
                const shipAloneOcp = find(getPromiseDates.truckable.ocpGroupItems[item].items, {
                    product: { shipAlone: true },
                });
                truckProducts.push(
                    <div>
                        <p
                            className={dt([
                                'font-open-sans',
                                'text-small',
                                index > 0 ? 'pt-4' : '',
                            ])}
                        >
                            {getPromiseDates.truckable.ocpGroupItems[item]?.label}
                        </p>
                        <div className={dt(['pt-2'])}>
                            <ItemThumbnailsList
                                orderItems={orderItems}
                                items={getPromiseDates.truckable.ocpGroupItems[item].items}
                                automationId="at-ocp-truck-thumb-list"
                                enableExposedPackagingMessage={enableExposedPackagingMessage}
                                isPromseDateDelivery
                            />
                            {shipAloneOcp && (
                                <WarningMessage
                                    isShowIcon
                                    messageText={EXPOSED_PACKAGING_CHECKOUT_MSG}
                                    automationId="at-exposed-packaging-block"
                                    isCheckout
                                    wrapperClassName={dt(['pb-2'])}
                                />
                            )}
                        </div>
                    </div>
                );
            });
        }
        return (
            <>
                {isOcpPage && isEmpty(getPromiseDates.regular) && (
                    <p className={dt(['font-open-sans-semibold', 'text-medium', 'pb-4', 'pt-6'])}>
                        Ship to Home
                    </p>
                )}
                {!isOcpPage ? (
                    <SilverItemDeliveryInformation
                        charge={
                            deliveryMethod?.charge &&
                            currencyFormatter.format(deliveryMethod?.charge, {
                                code: CURRENCY_US,
                            })
                        }
                        truckableDeliveryMessage={truckableDeliveryMessage}
                        isNewCheckoutRedesign
                    />
                ) : (
                    <div>
                        <p
                            className={dt([
                                'font-open-sans-semibold',
                                'text-medium',
                                'mb-1',
                                isEmpty(getPromiseDates.regular) ? 'pt-2' : 'pt-4',
                            ])}
                        >
                            Truck Delivery
                        </p>
                        {truckProducts}
                    </div>
                )}
                {!isOcpPage && (
                    <div className={dt(['pt-2'])}>
                        <ItemThumbnailsList
                            orderItems={orderItems}
                            items={getPromiseDates.truckable.items}
                            automationId="at-review-shipping-thumb-list"
                            enableExposedPackagingMessage={enableExposedPackagingMessage}
                            isPromseDateDelivery
                        />
                        {shipAlone && (
                            <WarningMessage
                                isShowIcon
                                messageText={EXPOSED_PACKAGING_CHECKOUT_MSG}
                                automationId="at-exposed-packaging-block"
                                isCheckout
                                wrapperClassName={dt(['pb-2'])}
                            />
                        )}
                    </div>
                )}
                {isOcpPage && (
                    <div className={dt(['h-[1px]', 'bg-gray-15', 'py-0', 'px-4', 'mt-2'])} />
                )}
            </>
        );
    };
    const renderRegularProducts = () => {
        const regularProducts = [];
        const regularItems = Object.keys(getPromiseDates.regular);
        regularItems.sort(dateComparison);
        regularItems.forEach((items, index) => {
            const shipAlone = find(getPromiseDates.regular[items].items, {
                product: { shipAlone: true },
            });
            regularProducts.push(
                <div>
                    {isOcpPage && index === 0 && (
                        <>
                            <p
                                className={dt([
                                    'font-open-sans-semibold',
                                    'text-medium',
                                    'pt-6',
                                    'pb-4',
                                ])}
                            >
                                {getPromiseDates.regular[items].deliveryCode === 'HOME'
                                    ? 'Ship to Home'
                                    : 'Ship to Store'}
                            </p>
                            {getPromiseDates?.regular[items]?.storeName ? (
                                <div className={dt(['flex', 'items-baseline'])}>
                                    <div className={dt(['mr-1', 'text-gray-70'])}>
                                        <Icon
                                            iconType="svg"
                                            width="20px"
                                            height="20px"
                                            viewBox="0 0 48 48"
                                        >
                                            <LocationPin />
                                        </Icon>
                                    </div>
                                    <div>
                                        <p
                                            className={dt([
                                                'font-open-sans-semibold',
                                                'text-small',
                                                'pb-1',
                                            ])}
                                            data-automation-id="at-ocp-bopis-promise-delivery-date-storename"
                                        >
                                            {getPromiseDates?.regular[items]?.storeName}
                                        </p>
                                        <p
                                            className={dt([
                                                'font-open-sans',
                                                'text-small',
                                                'pb-2',
                                                'text-gray-70',
                                            ])}
                                        >
                                            We’ll notify you when ready for pickup.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                ''
                            )}
                        </>
                    )}
                    <p
                        className={dt([
                            'font-open-sans-semibold',
                            'text-small',
                            'pb-0',
                            (isOcpPage && index === 0) ||
                            (!reviewPage && index === 0 && !isSingleShippingMethod)
                                ? 'pt-2'
                                : 'pt-4',
                        ])}
                        data-automation-id="at-regular-item-delivery-label"
                    >
                        {/* Added additional check based on shipping type when we switch between STS and STH in shipping section */}
                        {`${
                            getPromiseDates.regular[items].label ||
                            (shippingType === 'STORE' ? 'Estimated Pick Up: ' : '')
                        }${getPromiseDates.regular[items].deliveryPromiseMessage}`}
                    </p>
                    <div className={dt(['pt-2'])}>
                        <ItemThumbnailsList
                            orderItems={orderItems}
                            items={getPromiseDates.regular[items].items}
                            automationId="at-review-shipping-thumb-list"
                            enableExposedPackagingMessage={enableExposedPackagingMessage}
                            isPromseDateDelivery
                        />
                    </div>
                    {shipAlone && (
                        <WarningMessage
                            isShowIcon
                            messageText={EXPOSED_PACKAGING_CHECKOUT_MSG}
                            automationId="at-exposed-packaging-block"
                            isCheckout
                            wrapperClassName={dt(['pb-2'])}
                        />
                    )}
                </div>
            );
        });
        let showFree = false;
        let price = '';
        /* istanbul ignore else */
        if (deliveryMethod) {
            price = deliveryMethod.charge;
            showFree = deliveryMethod.charge === '$0.00';
        }
        return (
            <>
                {showStandardDeliveryTextForRegularItems && (
                    <div className={dt(['pl-0', 'inline-block', 'pt-6'])}>
                        <span
                            className={dt(['font-open-sans-semibold', 'text-medium', 'capitalize'])}
                        >
                            {/* if order has truck items then default it to Satndard delivery */}
                            {reviewPage && isEmpty(getPromiseDates.truckable.items)
                                ? `${deliveryMethod.duration}:`
                                : checkoutStaticText.STANDARD_DELIVERY}
                            &nbsp;
                        </span>
                        {reviewPage && isEmpty(getPromiseDates.truckable.items) && showFree && (
                            <span
                                className={dt([
                                    'mr-1',
                                    'text-brand-red',
                                    'font-open-sans-semibold',
                                    'text-medium',
                                ])}
                            >
                                FREE
                            </span>
                        )}
                        {reviewPage && isEmpty(getPromiseDates.truckable.items) && !showFree && (
                            <span
                                className={dt(['mr-1', 'font-open-sans-semibold', 'text-medium'])}
                            >
                                {price && currencyFormatter.format(price, { code: CURRENCY_US })}
                            </span>
                        )}
                    </div>
                )}
                {regularProducts}
                {((isOcpPage && isEmpty(getPromiseDates.truckable.ocpGroupItems)) ||
                    (!isOcpPage && !isEmpty(getPromiseDates.truckable.items))) && (
                    <div
                        className={dt([
                            'h-[1px]',
                            'bg-gray-15',
                            'py-0',
                            'px-4',
                            isOcpPage ? 'mt-2' : 'mt-4',
                        ])}
                    />
                )}
            </>
        );
    };
    const showTruckProducts =
        (!isOcpPage && !isEmpty(getPromiseDates.truckable.items)) ||
        (isOcpPage && !isEmpty(getPromiseDates.truckable.ocpGroupItems));
    return (
        <div>
            {isOcpPage && !isEmpty(getPromiseDates.bopis.items) && renderBopisProducts()}
            {!isEmpty(getPromiseDates.regular) && renderRegularProducts()}
            {showTruckProducts && renderTruckableProducts()}
        </div>
    );
};

PromiseDeliveryDates.propTypes = {
    orderItems: PropTypes.objectOf(PropTypes.object),
    enableExposedPackagingMessage: PropTypes.bool,
    truckableDeliveryMessage: PropTypes.string,
    reviewPage: PropTypes.bool,
    shippingMethods: PropTypes.arrayOf(PropTypes.object),
    orderCharges: PropTypes.objectOf(PropTypes.string),
    isOcpPage: PropTypes.bool,
    shippingType: PropTypes.string,
    orderShippingGroupsInfo: PropTypes.arrayOf(PropTypes.object),
    isSingleShippingMethod: PropTypes.bool,
};

PromiseDeliveryDates.defaultProps = {
    orderItems: {},
    enableExposedPackagingMessage: true,
    truckableDeliveryMessage: '',
    reviewPage: false,
    shippingMethods: {},
    orderCharges: {},
    isOcpPage: false,
    shippingType: '',
    orderShippingGroupsInfo: [],
    isSingleShippingMethod: false,
};

const mapStateToProps = (state) => ({
    shipToHomeInfo: state?.newShipping?.shipToHomeInfo,
    truckableDeliveryMessage: get(state, 'context.preferences.truckableDeliveryMessage', ''),
    enableExposedPackagingMessage: get(
        state,
        'context.featureFlags.enableExposedPackagingMessage',
        true
    ),
    shippingMethods: get(state, 'newShipping.shipToHomeInfo.shippingMethods', []),
    orderShippingGroupsInfo: state.orderShippingGroupsInfo,
});

export default connect(mapStateToProps)(PromiseDeliveryDates);
